import { useMemo } from 'react'
import type { OptionGenericType } from '../typeV2'
import { SELECT_ALL_OPTION } from './OtherComponentTypeFilterNodeFactoryV2'

/**
 * Hook to determine the default value for the component type filter.
 *
 * Deterministic logic:
 * 1. If user has already made a selection, use that selection
 * 2. If a target type is specified and exists in available options, use that
 * 3. If "All" option exists in available options, fallback to "All"
 * 4. Otherwise, fallback to SELECT_ALL_OPTION.value
 */
export const useOtherComponentTypeDefaultValue = (
  availableOptions: OptionGenericType<string>[],
  selectedTypes: Set<string>,
  targetType?: string
): string | Set<string> => {
  return useMemo(() => {
    // 1. If user has made a selection, respect it
    if (selectedTypes.size > 0) {
      return selectedTypes
    }

    // 2. If there's a target type match in available options, use it
    if (targetType) {
      const matchedOption = availableOptions.find((option) => option.value === targetType)
      if (matchedOption) {
        return matchedOption.value
      }
    }

    // 3. Check if "All" option exists in available options
    const hasAllOption = availableOptions.some((option) => option.value === SELECT_ALL_OPTION.value)
    if (hasAllOption) {
      return SELECT_ALL_OPTION.value
    }

    // 4. Fallback to SELECT_ALL_OPTION.value (should always be available)
    return SELECT_ALL_OPTION.value
  }, [availableOptions, selectedTypes, targetType])
}
